import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import { auth } from "../../firebase";
import { userService } from "../../services/firestoreService";
import {
  FiFileText,
  FiClock,
  FiBell,
  FiCalendar,
  FiLock,
} from "react-icons/fi";

function ProfileNotifications({ sharedUserDataCacheRef }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [settings, setSettings] = useState({
    educational: true,
    events: true,
    announcements: true,
    appointments: true,
    dataPrivacy: true,
  });
  const [timestamps, setTimestamps] = useState({
    educationalEnabledAt: null,
    eventsEnabledAt: null,
    announcementsEnabledAt: null,
    appointmentsEnabledAt: null,
    dataPrivacyEnabledAt: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notification settings from Firestore
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Try to get from shared cache first
        const cachedData = sharedUserDataCacheRef?.current;

        if (cachedData && cachedData.uid === user.uid) {
          if (cachedData.notificationSettings) {
            setSettings(cachedData.notificationSettings);
          }
          if (cachedData.notificationTimestamps) {
            setTimestamps(cachedData.notificationTimestamps);
          }
          // If cache has the data, don't fetch from Firestore
          if (cachedData.notificationSettings && cachedData.notificationTimestamps) {
            return;
          }
        }

        // Only fetch from Firestore if cache doesn't have the data
        const freshUserData = await userService.getUser(user.uid);
        if (freshUserData) {
          if (freshUserData.notificationSettings) {
            setSettings(freshUserData.notificationSettings);
          }
          if (freshUserData.notificationTimestamps) {
            setTimestamps(freshUserData.notificationTimestamps);
          }
          // Update cache with fresh data
          if (sharedUserDataCacheRef?.current) {
            sharedUserDataCacheRef.current = {
              ...sharedUserDataCacheRef.current,
              notificationSettings: freshUserData.notificationSettings,
              notificationTimestamps: freshUserData.notificationTimestamps
            };
          }
        }
      } catch (error) {
        console.error('Error fetching notification settings:', error);
      }
    };
    fetchSettings();
  }, [sharedUserDataCacheRef]);

  const toggleSetting = async (key) => {
    setIsLoading(true);
    const oldValue = settings[key];
    try {
      const user = auth.currentUser;
      if (!user) return;

      const newSettings = { ...settings, [key]: !settings[key] };
      const newTimestamps = { ...timestamps };

      // When toggle is turned ON, set the timestamp checkpoint
      if (newSettings[key]) {
        const timestampField = `${key}EnabledAt`;
        newTimestamps[timestampField] = new Date().toISOString();
      }

      setSettings(newSettings);
      setTimestamps(newTimestamps);

      await userService.updateUser(user.uid, {
        notificationSettings: newSettings,
        notificationTimestamps: newTimestamps
      });

      // Firestore optimization: Update shared cache with new settings
      if (sharedUserDataCacheRef?.current) {
        sharedUserDataCacheRef.current = {
          ...sharedUserDataCacheRef.current,
          notificationSettings: newSettings,
          notificationTimestamps: newTimestamps
        };
      }
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setSettings(prev => ({ ...prev, [key]: oldValue })); // revert on error
    } finally {
      setIsLoading(false);
    }
  };

  const notificationItems = [
    {
      key: "educational",
      title: t.educationalContent,
      desc: t.educationalContentDesc,
      icon: <FiFileText />,
      priority: "medium"
    },
    {
      key: "events",
      title: t.eventsActivities,
      desc: t.eventsActivitiesDesc,
      icon: <FiClock />,
      priority: "high"
    },
    {
      key: "announcements",
      title: t.systemAnnouncements,
      desc: t.systemAnnouncementsDesc,
      icon: <FiBell />,
      priority: "high"
    },
    {
      key: "appointments",
      title: t.appointmentReminders,
      desc: t.appointmentRemindersDesc,
      icon: <FiCalendar />,
    },
    {
      key: "dataPrivacy",
      title: t.dataPrivacy,
      desc: t.dataPrivacyDesc,
      icon: <FiLock />,
    },
  ];

  return (
    <div className="notification-panel">
      {/* Header Section */}
      <div className="notification-header">
        <div className="notification-header-text">
          <h2 className="notification-title">{t.notificationSettings}</h2>
          <p className="notification-subtitle">
            {t.chooseWhatNotifications}
          </p>
          <div className="notification-actions">
            <button
              className="notification-btn notification-btn--secondary"
              onClick={() => {
                // Disable all notifications
                const allDisabled = Object.keys(settings).reduce((acc, key) => {
                  acc[key] = false;
                  return acc;
                }, {});
                // Clear all timestamps when disabling all
                const allTimestampsCleared = Object.keys(timestamps).reduce((acc, key) => {
                  acc[key] = null;
                  return acc;
                }, {});
                setSettings(allDisabled);
                setTimestamps(allTimestampsCleared);
                // Save to Firestore
                auth.currentUser && userService.updateUser(auth.currentUser.uid, {
                  notificationSettings: allDisabled,
                  notificationTimestamps: allTimestampsCleared
                });
                // Firestore optimization: Update shared cache
                if (sharedUserDataCacheRef?.current) {
                  sharedUserDataCacheRef.current = {
                    ...sharedUserDataCacheRef.current,
                    notificationSettings: allDisabled,
                    notificationTimestamps: allTimestampsCleared
                  };
                }
              }}
            >
              {t.disableAll}
            </button>
            <button
              className="notification-btn notification-btn--primary"
              onClick={() => {
                // Enable all notifications
                const allEnabled = Object.keys(settings).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {});
                setSettings(allEnabled);
                // Save to Firestore
                auth.currentUser && userService.updateUser(auth.currentUser.uid, {
                  notificationSettings: allEnabled
                });
                // Firestore optimization: Update shared cache
                if (sharedUserDataCacheRef?.current) {
                  sharedUserDataCacheRef.current = {
                    ...sharedUserDataCacheRef.current,
                    notificationSettings: allEnabled
                  };
                }
              }}
            >
              {t.enableAll}
            </button>
          </div>
        </div>
        <div className="notification-stats">
          <div className="notification-stat">
            <span className="stat-number">
              {Object.values(settings).filter(Boolean).length}
            </span>
            <div className="stat-meta">
              <span className="stat-dot stat-dot--enabled"></span>
              <span className="stat-label">{t.enabled}</span>
            </div>
          </div>
          <div className="notification-stat">
            <span className="stat-number">
              {Object.values(settings).filter(v => !v).length}
            </span>
            <div className="stat-meta">
              <span className="stat-dot stat-dot--disabled"></span>
              <span className="stat-label">{t.disabled}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Categories */}
      <div className="notification-categories">
        {notificationItems.map((item, index) => (
          <div key={item.key} className="notification-category">
            <div className="notification-category-header">
              <div className="notification-icon-wrapper">
                <div className="notification-icon">
                  {item.icon}
                </div>
              </div>
              <h3 className="notification-category-title">{item.title}</h3>
              <div className="notification-toggle">
                <button
                  className="notification-switch"
                  onClick={() => toggleSetting(item.key)}
                  aria-checked={settings[item.key]}
                  role="switch"
                  disabled={isLoading}
                >
                  <span
                    className="notification-slider"
                    style={{
                      backgroundColor: settings[item.key] ? '#3b82f6' : '#e5e7eb'
                    }}
                  >
                    <span
                      className="notification-thumb"
                      style={{
                        transform: settings[item.key] ? 'translateX(22px)' : 'translateX(2px)'
                      }}
                    />
                  </span>
                </button>
              </div>
            </div>

            <div className="notification-category-footer">
              <p className="notification-category-description">{item.desc}</p>
              {item.priority === 'high' && (
                <div className="priority-badge">High Priority</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProfileNotifications;
